import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Layout, Typography, Button, Spin, message, Space, Dropdown, Avatar, Grid, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReloadOutlined, UserOutlined, MoreOutlined, ClearOutlined, CloseOutlined } from '@ant-design/icons';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import MessageFooter from '../../../Messages/Messages/MessageFooter/MessageFooter';
import MessageList from '../../../Messages/Messages/MessageContent/MessageList/MessageList';
import MessageDetailsDrawer from '../../../Messages/Messages/MessageDetailsDrawer/MessageDetailsDrawer';
import useSocketSubscription from '../../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../../Hooks/useSocketEvent';
import { Resource, Action } from '../../../../Constants/socket';
import { getMessages } from '../../../Messages/MessagesServices';
import TestHeaderActions from '../TestHeaderActions';
import { getVirtualContactConversation } from '../../AgentTestingService';
import { resolveBackendMessage } from '../../../../Utils/apiMessages';
import CountdownPill from '../../../Shared/CountdownPill';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Typing animation keyframes (copied from MessageContent to avoid breaking existing component)
const typingAnimationStyle = `
  @keyframes typingAnimation {
    0% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0); }
  }
`;


// Local TypingIndicator for TestChatInterface (copied to avoid breaking MessageContent)
const TestTypingIndicator = ({ isDarkMode }) => {
    const { t } = useTranslation();

    const bubbleStyle = {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: isDarkMode ? '#303030' : '#f0f0f0',
        borderRadius: '18px',
        padding: '10px 16px',
        maxWidth: '80%',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    };

    const bubblePointerStyle = {
        content: '""',
        position: 'absolute',
        bottom: '8px',
        left: '-6px',
        width: '12px',
        height: '12px',
        backgroundColor: isDarkMode ? '#303030' : '#f0f0f0',
        transform: 'rotate(45deg)',
        borderRadius: '2px',
    };

    const dotBaseStyle = {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: '#1890ff',
        display: 'inline-block',
        margin: '0 2px',
    };

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            padding: '8px 0'
        }}>
            <style dangerouslySetInnerHTML={{ __html: typingAnimationStyle }} />
            <Avatar
                size="small"
                icon={<UserOutlined />}
                style={{ marginRight: '8px' }}
            />
            <div style={bubbleStyle}>
                <div style={{ ...bubblePointerStyle }} />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                        style={{
                            ...dotBaseStyle,
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0s'
                        }}
                    />
                    <div
                        style={{
                            ...dotBaseStyle,
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0.2s'
                        }}
                    />
                    <div
                        style={{
                            ...dotBaseStyle,
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0.4s'
                        }}
                    />
                    <Text style={{ marginLeft: '8px', color: '#1890ff' }}>
                        {t('agent_is_typing')}
                    </Text>
                </div>
            </div>
        </div>
    );
};

const TestCountdownIndicator = ({ isDarkMode, seconds }) => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        padding: '8px 0'
    }}>
        <CountdownPill
            seconds={seconds}
            isDarkMode={isDarkMode}
            size="md"
            animationKey={seconds}
        />
    </div>
);

const TestChatInterface = ({ agent, conversation, onSendMessage, onStartNewTest, isMobile: propIsMobile, onConversationUpdated }) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const screens = useBreakpoint() || {};
    const [messages, setMessages] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const pageSize = 10;
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isFetchingMoreTop, setIsFetchingMoreTop] = useState(false);
    const [localConversation, setLocalConversation] = useState(conversation);
    const [draftAttachments, setDraftAttachments] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const messageRefs = useRef({});
    const messagesContainerRef = useRef(null);
    const listScrollApiRef = useRef(null);
    const footerContainerRef = useRef(null);
    const footerFocusRef = useRef(null);
    const [footerHeight, setFooterHeight] = useState(0);
    const [replyTo, setReplyTo] = useState(null); // message object to reply to


    const themeColors = {
        background: isDarkMode ? '#141414' : '#fff',
        headerBackground: isDarkMode ? '#1f1f1f' : '#fff',
        headerBorder: isDarkMode ? '#434343' : '#f0f0f0',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        border: isDarkMode ? '#434343' : '#f0f0f0'
    };

    const rawStartsIn = localConversation?.processing_starts_in ?? 0;
    const initialStartsIn = useMemo(() => {
        const next = parseInt(String(rawStartsIn), 10);
        return Number.isFinite(next) && next > 0 ? next : 0;
    }, [rawStartsIn]);
    const [secondsUntilProcessing, setSecondsUntilProcessing] = useState(initialStartsIn);
    const countdownEndsAtRef = useRef(0);
    const countdownVersionRef = useRef(0);
    const [countdownVersion, setCountdownVersion] = useState(0);

    const processedCountdown = useMemo(() => {
        if (localConversation?.is_writing) return 0;
        const next = parseInt(String(rawStartsIn), 10);
        return Number.isFinite(next) && next > 0 ? next : 0;
    }, [rawStartsIn, localConversation?.is_writing]);

    useEffect(() => {
        const s = processedCountdown;
        if (!s || s <= 0) {
            countdownEndsAtRef.current = 0;
            setSecondsUntilProcessing(0);
            return;
        }
        countdownEndsAtRef.current = Date.now() + s * 1000;
        setSecondsUntilProcessing(s);
        countdownVersionRef.current += 1;
        setCountdownVersion(countdownVersionRef.current);
    }, [processedCountdown, localConversation?.id]);

    useEffect(() => {
        if (!countdownEndsAtRef.current) return;
        const id = setInterval(() => {
            const remainingMs = countdownEndsAtRef.current - Date.now();
            const next = remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0;
            setSecondsUntilProcessing((prev) => (prev === next ? prev : next));
            if (next <= 0) {
                countdownEndsAtRef.current = 0;
                clearInterval(id);
            }
        }, 200);
        return () => clearInterval(id);
    }, [countdownVersion]);

    // Scroll helper: always scroll to bottom of the messages container
    const scrollToBottom = useCallback(() => {
        try {
            if (listScrollApiRef.current && typeof listScrollApiRef.current.scrollToBottom === 'function') {
                listScrollApiRef.current.scrollToBottom();
                return;
            }
            const el = messagesContainerRef.current;
            if (!el) return;
            requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
        } catch (e) { }
    }, []);

    // Prepare virtual list data including temporary indicators for TestChatInterface
    const virtualListData = useMemo(() => {
        const data = [];

        // Add typing indicator as first item if agent is writing
        if (localConversation?.is_writing && messages.length > 0) {
            data.push({
                id: 'test-typing-indicator',
                type: 'typing-indicator',
                isTemporary: true,
                isDarkMode
            });
        }

        // Add countdown indicator as first item if countdown is active
        if (!localConversation?.is_writing && secondsUntilProcessing > 0 && messages.length > 0) {
            data.push({
                id: 'test-countdown-indicator',
                type: 'countdown-indicator',
                isTemporary: true,
                isDarkMode,
                seconds: secondsUntilProcessing
            });
        }

        // Add all messages after the indicators
        data.push(...messages);

        return data;
    }, [messages, localConversation?.is_writing, secondsUntilProcessing, isDarkMode]);

    // Subscribe to messages for this test conversation
    const conversationIds = conversation?.id ? [conversation.id] : [];
    useSocketSubscription(Resource.CONVERSATION, conversationIds, true);

    // Subscribe to specific message IDs for granular updates
    const messageIds = useMemo(() => messages.map(m => m.id).filter(Boolean), [messages]);
    useSocketSubscription(Resource.MESSAGE, messageIds, true);

    // Sync local conversation state when prop changes
    useEffect(() => {
        setLocalConversation(conversation);

        // Load draft attachments from conversation.file_uploads
        const drafts = conversation?.file_uploads || [];
        if (drafts.length > 0) {
            const fileListItems = drafts.map((draft) => {
                const isImage = draft.mime_type?.startsWith('image/');
                return {
                    uid: `-draft-${draft.id}`,
                    name: draft.original_filename || 'file',
                    status: 'done',
                    url: isImage ? draft.file_url : null,
                    thumbUrl: isImage ? draft.file_url : null,
                    fileId: draft.id,
                    fileData: draft,
                    size: draft.size,
                    type: draft.mime_type
                };
            });
            setDraftAttachments(fileListItems);
        } else {
            setDraftAttachments([]);
        }
    }, [conversation]);

    // Load existing messages when conversation changes
    useEffect(() => {
        const loadMessages = async () => {
            if (!conversation?.id) {
                return;
            }

            setLoading(true);
            try {
                const response = await getMessages({
                    conversation_id: conversation.id,
                    page: 1,
                    size: pageSize
                });



                if (response.data && response.data.length > 0) {
                    // Messages come sorted desc from API, keep them in desc order for MessageList
                    // MessageList component will reverse them for display (oldest at top, newest at bottom)
                    const sortedMessages = response.data.sort((a, b) =>
                        new Date(b.sent_at) - new Date(a.sent_at)
                    );

                    setMessages(sortedMessages);
                    setPage(1);
                    setHasMore(response.data.length === pageSize);
                } else {
                    setMessages([]);
                    setPage(1);
                    setHasMore(false);
                }
            } catch (error) {
                message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_load_messages'));
            } finally {
                setLoading(false);
            }
        };

        loadMessages();
    }, [conversation?.id]);

    const loadMoreTop = useCallback(async () => {
        if (!conversation?.id) return;
        if (isFetchingMoreTop || !hasMore) return;
        setIsFetchingMoreTop(true);
        try {
            const nextPage = page + 1;
            const response = await getMessages({
                conversation_id: conversation.id,
                page: nextPage,
                size: pageSize
            });
            const newItems = Array.isArray(response?.data) ? response.data : [];
            if (newItems.length > 0) {
                const merged = [...messages];
                newItems.forEach((m) => {
                    if (!merged.some((ex) => ex.id === m.id)) {
                        merged.push(m);
                    }
                });
                const sorted = merged.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
                setMessages(sorted);
                setPage(nextPage);
                setHasMore(newItems.length === pageSize);
            } else {
                setHasMore(false);
            }
        } catch (e) {
            // silent fail keeps UX smooth
        } finally {
            setIsFetchingMoreTop(false);
        }
    }, [conversation?.id, isFetchingMoreTop, hasMore, page, pageSize, messages]);

    const handleListScroll = useCallback((e) => {
        try {
            const el = e?.currentTarget;
            if (!el) return;
            if (el.scrollTop === 0 && hasMore && !isFetchingMoreTop) {
                loadMoreTop();
            }
        } catch (_) { }
    }, [hasMore, isFetchingMoreTop, loadMoreTop]);

    // Keep the drawer's selected message in sync with live updates
    useEffect(() => {
        if (selectedMessage?.id) {
            const updated = messages.find(m => m.id === selectedMessage.id);
            if (updated && updated !== selectedMessage) {
                setSelectedMessage(updated);
            }
            if (!updated && drawerVisible) {
                setDrawerVisible(false);
                setSelectedMessage(null);
            }
        }
    }, [messages, selectedMessage?.id, drawerVisible]);

    // Handle new messages via socket
    useSocketEvent(Resource.MESSAGE, Action.CREATE, useCallback((data) => {

        if (data.conversation_id === conversation?.id) {
            setMessages(prev => {
                // Avoid duplicates
                if (prev.some(msg => msg.id === data.id)) {
                    return prev;
                }
                const newMessages = [...prev, data];
                // Sort in descending order (newest first) for MessageList component
                return newMessages.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
            });

            // Scroll to bottom for new messages
            setTimeout(() => {
                if (messagesContainerRef.current) {
                    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                }
            }, 100);
        }
    }, [conversation?.id]));

    // Handle message updates via socket
    useSocketEvent(Resource.MESSAGE, Action.UPDATE, useCallback((data) => {
        if (data.conversation_id === conversation?.id) {
            setMessages(prev => prev.map(msg =>
                msg.id === data.id ? { ...msg, ...data } : msg
            ));
        }
    }, [conversation?.id]));

    // Handle message deletions via socket
    useSocketEvent(Resource.MESSAGE, Action.DELETE, useCallback((data) => {
        if (data.conversation_id === conversation?.id) {
            setMessages(prev => prev.filter(msg => msg.id !== data.id));
        }
    }, [conversation?.id]));

    // Handle conversation updates (for typing indicator)
    useSocketEvent(Resource.CONVERSATION, Action.UPDATE, useCallback((data) => {
        if (data.id === conversation?.id) {
            setLocalConversation((prev) => {
                const next = {
                    ...(prev || {}),
                    ...data
                };
                try {
                    if (typeof onConversationUpdated === 'function') {
                        onConversationUpdated(next);
                    }
                } catch (e) { }
                return next;
            });
        }
    }, [conversation?.id, onConversationUpdated]));

    // Focus footer input via ref (React way, no document access)
    const focusFooterInput = useCallback(() => {
        try {
            if (footerFocusRef.current) {
                footerFocusRef.current();
            }
        } catch (e) { }
    }, []);

    // Scroll to original message when clicking quoted preview
    const handleReplyClick = useCallback((replyToMessage) => {
        if (!replyToMessage) return;
        const original = messages.find(
            m => m.sent_at === replyToMessage.sent_at && m.sender_type === replyToMessage.sender_type
        );
        if (original?.id && messageRefs.current[original.id]?.current) {
            messageRefs.current[original.id].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [messages, messageRefs]);

    // Start replying to a specific message
    const handleReplyAction = useCallback((messageItem) => {
        setReplyTo(messageItem);
        focusFooterInput();
    }, [focusFooterInput]);

    const handleSendTestMessage = useCallback(async (messageData) => {
        if (!messageData.message?.trim() && (!messageData.attachment_ids || messageData.attachment_ids.length === 0)) {
            return;
        }

        // Immediately scroll to bottom on send action
        scrollToBottom();

        setIsSending(true);
        try {
            // Transform MessageFooter format { message, attachment_ids } to API format { content, attachment_ids }
            const apiPayload = {
                content: messageData.message || '',
                attachment_ids: messageData.attachment_ids || []
            };
            if (replyTo?.id) {
                apiPayload.reply_to_message_id = replyTo.id;
            }

            const result = await onSendMessage(apiPayload);

            // Add the customer message immediately to the UI
            // Handle both API response formats: direct result or wrapped in data
            const responseData = result?.data || result;
            const customerMessage = responseData?.customer_message;

            if (customerMessage) {
                setMessages(prev => {
                    // Check if message already exists to prevent duplicates
                    const messageExists = prev.some(msg => msg.id === customerMessage.id);
                    if (messageExists) {
                        return prev;
                    }

                    const newMessages = [...prev, customerMessage];
                    // Sort in descending order (newest first) for MessageList component
                    const sortedMessages = newMessages.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
                    // Ensure we stay pinned to bottom right after inserting
                    setTimeout(scrollToBottom, 0);
                    return sortedMessages;
                });
            } else {
                message.warning(t('message_sent_but_no_response_received'));
            }

            // Clear reply context and draft attachments after sending
            setDraftAttachments([]);
            setReplyTo(null);

            // Agent will start typing, handled by socket events automatically
            // Typing indicator will show when is_writing becomes true

        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_send_message'));
        } finally {
            setIsSending(false);
        }
    }, [onSendMessage, t, replyTo?.id, scrollToBottom]);



    const handleClearHistory = useCallback(() => {
        setMessages([]);
        message.success(t('test_history_cleared'));
    }, [t]);

    // MessageList handles all scrolling internally via lastMessageRef callback

    // Measure footer height to reserve space like Messages page
    useEffect(() => {
        const measure = () => {
            const h = footerContainerRef.current ? footerContainerRef.current.offsetHeight || 0 : 0;
            setFooterHeight(h);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    // Mock conversation data structure to match what MessageList expects
    const mockConversation = {
        id: conversation?.id,
        contact: {
            id: conversation?.contact?.id,
            first_name: conversation?.contact?.first_name || 'Test',
            last_name: conversation?.contact?.last_name || 'Customer',
            profile_picture_url: null
        }
    };

    // Mobile responsive layout configuration
    const isMobile = propIsMobile !== undefined ? propIsMobile : !screens.sm;
    const layoutHeight = '100%';

    return (
        <Layout style={{
            height: layoutHeight,
            background: themeColors.background,
            paddingTop: '0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <Header
                style={{
                    background: themeColors.headerBackground,
                    borderBottom: `1px solid ${themeColors.headerBorder}`,
                    padding: isMobile ? '0 16px' : '0 24px 0 16px',
                    height: isMobile ? '56px' : '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0
                }}
            >
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    flex: 1,
                    marginRight: '16px'
                }}>
                    <Avatar
                        size={isMobile ? 28 : 32}
                        src={localConversation?.contact?.profile_picture_url || localConversation?.contact?.picture_url || null}
                        icon={<UserOutlined />}
                        style={{ marginRight: isMobile ? '8px' : '12px', flexShrink: 0 }}
                    />
                    <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Title
                            level={5}
                            style={{
                                margin: 0,
                                color: themeColors.text,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: isMobile ? '13px' : '14px',
                                lineHeight: isMobile ? '18px' : '20px'
                            }}
                        >
                            {localConversation?.contact?.first_name} {localConversation?.contact?.last_name}
                        </Title>
                        <Text
                            type="secondary"
                            style={{
                                fontSize: isMobile ? '10px' : '11px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                                lineHeight: isMobile ? '14px' : '16px'
                            }}
                        >
                            {t('virtual_contact')}
                        </Text>
                    </div>
                </div>

                <Space size={isMobile ? 'xsmall' : 'small'} style={{ flexShrink: 0 }}>
                    <TestHeaderActions
                        screens={screens}
                        conversation={localConversation}
                        onContactUpdated={async () => {
                            try {
                                if (localConversation?.contact?.id) {
                                    const refreshed = await getVirtualContactConversation(localConversation.contact.id);
                                    setLocalConversation(refreshed);
                                }
                            } catch (e) { }
                        }}
                        onNewTest={onStartNewTest}
                    />

                </Space>
            </Header>

            {/* Messages container with typing indicator positioned correctly */}
            <div style={{
                position: 'relative',
                overflow: 'hidden',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Content
                    ref={messagesContainerRef}
                    style={{
                        padding: isMobile ? '12px' : '16px',
                        paddingBottom: isMobile ? '12px' : '16px',
                        overflow: 'auto',
                        background: themeColors.background,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                            <Spin size="large" />
                        </div>
                    ) : messages.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: isMobile ? '20px' : '40px',
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1
                        }}>
                            <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                {t('send_first_message_to_start_testing')}
                            </Text>
                        </div>
                    ) : (
                        <MessageList
                            messages={virtualListData}
                            loading={false}
                            messageRefs={messageRefs}
                            handleReplyClick={handleReplyClick}
                            onReplyAction={handleReplyAction}
                            scrollApiRef={listScrollApiRef}
                            setSelectedMessage={setSelectedMessage}
                            setDrawerVisible={setDrawerVisible}
                            invertMessageSides={true}
                            screens={screens}
                            bottomInset={(screens.xs ? 12 : 24)}
                            TypingIndicator={TestTypingIndicator}
                            CountdownIndicator={TestCountdownIndicator}
                            onScroll={handleListScroll}
                            hasMore={hasMore}
                            isFetchingMoreTop={isFetchingMoreTop}
                            conversationId={conversation?.id}
                        />
                    )}
                </Content>
            </div>

            {/* Message Details Drawer - using shared component */}
            <MessageDetailsDrawer
                screens={screens}
                drawerVisible={drawerVisible}
                selectedMessage={selectedMessage}
                setDrawerVisible={setDrawerVisible}
                setSelectedMessage={setSelectedMessage}
            />

            <div ref={footerContainerRef} style={{
                borderTop: `1px solid ${themeColors.border}`,
                background: themeColors.background,
                flexShrink: 0,
                zIndex: 10
            }}>
                {/* Reply preview above input */}
                {replyTo && (
                    <div style={{
                        padding: '8px 16px',
                        borderBottom: `1px dashed ${themeColors.border}`,
                        background: isDarkMode ? '#1f1f1f' : '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8
                    }}>
                        <div style={{
                            borderInlineStart: `2px solid ${isDarkMode ? '#FFD700' : '#1890ff'}`,
                            paddingInlineStart: 8,
                            overflow: 'hidden'
                        }}>
                            <div style={{ fontSize: 11, opacity: 0.75 }}>{t('replying_to')}</div>
                            <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'hidden' }}>
                                {replyTo.content || t('media_message')}
                            </div>
                        </div>
                        <Button size="small" onClick={() => setReplyTo(null)}>{t('cancel')}</Button>
                    </div>
                )}
                <MessageFooter
                    onSendMessage={handleSendTestMessage}
                    isSending={isSending}
                    focusInputRef={footerFocusRef}
                    conversationId={conversation?.id}
                    initialFileList={draftAttachments}
                />
            </div>


        </Layout>
    );
};

export default TestChatInterface; 